import { useEffect, useState } from 'react';
import Order, { ACCESSORY_ORDER_STATUSES, AccessoryOrderStatus, ORDER_SOURCE_LABELS } from '@/models/order';
import { useOrder, useUpdateOrder, useDeleteOrder, formatMoney, OrderUpdatePayload } from '@/services/orders';
import FormModal from '@/shared/components/form-modal';
import TrackingTimeline from '@/shared/components/TrackingTimeline';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { useSettings } from '@/services/settings';
import { formatDate } from '@/utils/helpers';
import { darkTextDim, errorBannerStyle } from '@/shared/dark-inline-styles';
import { ORDER_STATUS_LABELS } from '@/utils/order-status';

interface OrderModalProps {
    isOpen: boolean;
    selectedOrderId?: string;
    toggle: () => void;
}

interface OrderForm {
    status: AccessoryOrderStatus;
    /** Internal triage flag — never shown to the buyer, always paired with a note. */
    needsAttention: boolean;
    trackingNumber: string;
    carrier: string;
    trackingUrl: string;
    adminNote: string;
    // Ship-to is editable because a comped or off-platform order arrives with NO
    // address by construction — Stripe never collected one — and without this
    // such an order is unshippable forever.
    shippingName: string;
    phone: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
}

const EMPTY_FORM: OrderForm = {
    status: 'paid',
    needsAttention: false,
    trackingNumber: '',
    carrier: '',
    trackingUrl: '',
    adminNote: '',
    shippingName: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
};

const labelStyle: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: darkTextDim, margin: '0 0 2px' };
const valueStyle: React.CSSProperties = { fontSize: 14, margin: '0 0 10px', whiteSpace: 'pre-line' };

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
        <p style={labelStyle}>{label}</p>
        <p style={valueStyle}>{children}</p>
    </div>
);

const OrderModal = ({ isOpen, toggle, selectedOrderId }: OrderModalProps) => {
    const { data: orderRes, isLoading } = useOrder(selectedOrderId as string, isOpen);
    // Carrier list comes from the shared settings doc, so ops can add one without
    // a release. The API derives `trackingUrl` from the chosen carrier's template
    // when none is typed in.
    const { data: settingsRes } = useSettings();
    const carriers = settingsRes?.data?.shippingCarriers ?? [];
    const { mutateAsync: updateOrder, isPending: isUpdating } = useUpdateOrder();
    const { mutateAsync: deleteOrder, isPending: isDeleting } = useDeleteOrder();
    const order: Order | undefined = orderRes?.data ?? undefined;

    const [form, setForm] = useState<OrderForm>(EMPTY_FORM);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        if (order) {
            setForm({
                status: order.status,
                needsAttention: Boolean(order.needsAttention),
                trackingNumber: order.trackingNumber ?? '',
                carrier: order.carrier ?? '',
                trackingUrl: order.trackingUrl ?? '',
                adminNote: order.adminNote ?? '',
                shippingName: order.shippingName ?? '',
                phone: order.phone ?? '',
                line1: order.shippingAddress?.line1 ?? '',
                line2: order.shippingAddress?.line2 ?? '',
                city: order.shippingAddress?.city ?? '',
                state: order.shippingAddress?.state ?? '',
                postalCode: order.shippingAddress?.postal_code ?? '',
                country: order.shippingAddress?.country ?? '',
            });
        }
        setError(null);
    }, [isOpen, order]);

    // Reset the editable fields on close so re-opening a DIFFERENT order never
    // briefly shows the previous order's tracking/status while its data loads.
    useEffect(() => {
        if (!isOpen) {
            setForm(EMPTY_FORM);
            setError(null);
        }
    }, [isOpen]);

    const set = <K extends keyof OrderForm>(key: K, value: OrderForm[K]) => setForm((f) => ({ ...f, [key]: value }));

    const handleSave = async () => {
        if (!selectedOrderId) return;
        const payload: OrderUpdatePayload = {
            status: form.status,
            needsAttention: form.needsAttention,
            trackingNumber: form.trackingNumber.trim() || undefined,
            carrier: form.carrier.trim() || undefined,
            trackingUrl: form.trackingUrl.trim() || undefined,
            adminNote: form.adminNote.trim() || undefined,
            shippingName: form.shippingName.trim() || undefined,
            phone: form.phone.trim() || undefined,
        };

        // Mirror the API's rule at the field: a flag with no reason is a to-do
        // nobody can action. Checked here as well as server-side so the admin is
        // told before the round trip, not by a 400 afterwards.
        if (form.needsAttention && !form.adminNote.trim()) {
            setError('Add an internal note explaining why this order needs attention.');
            return;
        }

        // Send the address only when the REQUIRED parts are all present. A
        // half-written address is worse than none: it looks shippable and is not,
        // which is exactly the state the readiness check exists to catch.
        // `line2` and `state` are deliberately not required — plenty of valid
        // addresses worldwide have neither.
        const hasAddress = [form.line1, form.city, form.postalCode, form.country].every((v) => v.trim());
        if (hasAddress) {
            payload.shippingAddress = {
                line1: form.line1.trim(),
                line2: form.line2.trim() || undefined,
                city: form.city.trim(),
                state: form.state.trim() || undefined,
                postal_code: form.postalCode.trim(),
                country: form.country.trim(),
            };
        }
        try {
            // callApi never throws on API errors — inspect result.status.
            const result = await updateOrder({ orderId: selectedOrderId, ...payload });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to update the order.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to update order:', e);
            setError('Failed to update the order. Check the console for details.');
        }
    };

    const handleDelete = async () => {
        if (!selectedOrderId) return;
        try {
            const result = await deleteOrder({ orderId: selectedOrderId });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete the order.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to delete order:', e);
            setError('Failed to delete the order. Check the console for details.');
        }
    };

    const addr = order?.shippingAddress;
    const addressText = addr
        ? [order?.shippingName, addr.line1, addr.line2, [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', '), addr.country]
              .filter(Boolean)
              .join('\n')
        : '—';

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!selectedOrderId}
            title={order ? `Order ${order.orderNumber}` : 'Order'}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && <div style={errorBannerStyle}>{error}</div>}

                    {order && (
                        <>
                            {/* ── Customer + shipping ──────────────────────── */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, textAlign: 'left', marginBottom: 8 }}>
                                <Field label="Customer email">{order.email}</Field>
                                <Field label="Phone">{order.phone ?? '—'}</Field>
                                <Field label="Placed">{formatDate(order.created_at)}</Field>
                                <Field label="Current status">{ORDER_STATUS_LABELS[order.status]}</Field>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field label="Source">
                                        {ORDER_SOURCE_LABELS[order.source ?? 'accessories'] ?? order.source}
                                        {(order.source ?? 'accessories') === 'course33' && (
                                            <span style={{ color: '#999', marginLeft: 8 }}>
                                                Harmony Board included with a Course 33 purchase — the total is the course price, not a separate board
                                                charge.
                                            </span>
                                        )}
                                    </Field>
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field label="Ship to">{addressText}</Field>
                                </div>
                            </div>

                            {/* ── Line items ───────────────────────────────── */}
                            <p style={{ ...labelStyle, marginTop: 8 }}>Items</p>
                            <div style={{ border: '1px solid #e6e6e6', borderRadius: 6, padding: '6px 12px', marginBottom: 12, textAlign: 'left' }}>
                                {order.lineItems.map((li, i) => (
                                    <div
                                        key={`${li.stripePriceId ?? li.name}-${i}`}
                                        style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 14 }}
                                    >
                                        <span>
                                            {li.name} <span style={{ color: '#999' }}>× {li.quantity}</span>
                                        </span>
                                        <span>{formatMoney(li.amountSubtotal, li.currency || order.currency)}</span>
                                    </div>
                                ))}
                                <div style={{ borderTop: '1px solid #3a3a42', marginTop: 4, paddingTop: 6, fontSize: 13, color: '#bbbbbb' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Subtotal</span>
                                        <span>{formatMoney(order.subtotalAmount, order.currency)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Shipping</span>
                                        <span>{order.shippingAmount > 0 ? formatMoney(order.shippingAmount, order.currency) : 'Free'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Tax</span>
                                        <span>{formatMoney(order.taxAmount, order.currency)}</span>
                                    </div>
                                    <div
                                        style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#ffffff', marginTop: 4 }}
                                    >
                                        <span>Total</span>
                                        <span>{formatMoney(order.totalAmount, order.currency)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* ── Ship-to (editable) ───────────────────────── */}
                            <p style={{ margin: '12px 0 4px', fontWeight: 600, textAlign: 'left' }}>Ship to</p>
                            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#999999', textAlign: 'left' }}>
                                Comped and off-platform orders arrive with no address — Stripe never collected one. Line 1, city, postal code and
                                country are all required before an order counts as ready to ship.
                            </p>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <MaterialTextField
                                    label="Recipient name"
                                    value={form.shippingName}
                                    onChange={(e) => set('shippingName', e.target.value)}
                                />
                                <MaterialTextField label="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
                            </div>
                            <MaterialTextField label="Address line 1" value={form.line1} onChange={(e) => set('line1', e.target.value)} />
                            <MaterialTextField label="Address line 2 (optional)" value={form.line2} onChange={(e) => set('line2', e.target.value)} />
                            <div style={{ display: 'flex', gap: 16 }}>
                                <MaterialTextField label="City" value={form.city} onChange={(e) => set('city', e.target.value)} />
                                <MaterialTextField
                                    label="State / region (optional)"
                                    value={form.state}
                                    onChange={(e) => set('state', e.target.value)}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <MaterialTextField label="Postal code" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} />
                                <MaterialTextField
                                    label="Country (2-letter code)"
                                    value={form.country}
                                    onChange={(e) => set('country', e.target.value.toUpperCase())}
                                />
                            </div>

                            {/* ── Fulfillment (editable) ───────────────────── */}
                            <p style={{ margin: '12px 0 4px', fontWeight: 600, textAlign: 'left' }}>Fulfillment</p>
                            <MaterialTextField
                                name="select"
                                select
                                label="Status"
                                value={form.status}
                                onChange={(e) => set('status', e.target.value as AccessoryOrderStatus)}
                            >
                                {ACCESSORY_ORDER_STATUSES.map((s) => (
                                    <MaterialFormOption key={s} value={s}>
                                        {ORDER_STATUS_LABELS[s]}
                                    </MaterialFormOption>
                                ))}
                            </MaterialTextField>
                            <p style={{ margin: '-6px 0 10px', fontSize: 12, color: '#999999' }}>
                                Setting status to <strong>Shipped</strong> or <strong>Delivered</strong> notifies the customer on every channel that
                                is switched on and reachable — email, Telegram DM and an in-app push. Every attempt, including a skip, is written to
                                their CRM timeline.
                            </p>
                            <div style={{ display: 'flex', gap: 16 }}>
                                {/* Carriers are runtime DATA, not a hardcoded enum — which
                                    carrier serves where is an operations fact. Until ops fills
                                    the list in (Settings), this falls back to free text rather
                                    than trapping an order behind an empty dropdown. */}
                                {carriers.length > 0 ? (
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Carrier"
                                        value={form.carrier}
                                        onChange={(e) => set('carrier', e.target.value)}
                                    >
                                        <MaterialFormOption value="">No carrier</MaterialFormOption>
                                        {carriers.map((c) => (
                                            <MaterialFormOption key={c.name} value={c.name}>
                                                {c.name}
                                            </MaterialFormOption>
                                        ))}
                                    </MaterialTextField>
                                ) : (
                                    <MaterialTextField
                                        label="Carrier (e.g. DHL)"
                                        value={form.carrier}
                                        onChange={(e) => set('carrier', e.target.value)}
                                    />
                                )}
                                <MaterialTextField
                                    label="Tracking number"
                                    value={form.trackingNumber}
                                    onChange={(e) => set('trackingNumber', e.target.value)}
                                />
                            </div>
                            <MaterialTextField
                                label="Tracking URL (leave blank to derive it from the carrier)"
                                value={form.trackingUrl}
                                onChange={(e) => set('trackingUrl', e.target.value)}
                            />
                            <MaterialTextField
                                label="Internal note (not shown to the customer)"
                                multiline
                                value={form.adminNote}
                                onChange={(e) => set('adminNote', e.target.value)}
                            />
                            {/* Carrier scan history — the same component the Course CRM
                                drawer uses, so the two screens can never tell an admin
                                different stories about the same parcel. */}
                            <div style={{ textAlign: 'left' }}>
                                <TrackingTimeline
                                    events={order?.trackingEvents}
                                    statusDetail={order?.trackingStatusDetail}
                                    lastCheckedAt={order?.trackingLastCheckedAt}
                                    lastError={order?.trackingLastError}
                                    hasTrackingCode={Boolean(order?.trackingNumber)}
                                    orderId={order?.id}
                                />
                            </div>
                            {/* The triage flag is an OVERLAY on the status above, not a
                                replacement for it: a board that arrived and was then
                                disputed is still `delivered` AND still needs looking at.
                                It never reaches the buyer's order tracker. */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 0', textAlign: 'left', fontSize: 13 }}>
                                <input type="checkbox" checked={form.needsAttention} onChange={(e) => set('needsAttention', e.target.checked)} />
                                Needs attention (internal only — shows in the Course CRM's triage bucket)
                            </label>
                            {form.needsAttention && !form.adminNote.trim() && (
                                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#b26a00', textAlign: 'left' }}>
                                    Add an internal note above explaining why.
                                </p>
                            )}

                            {(order.refundId || order.refundedAt) && (
                                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#b26a00', textAlign: 'left' }}>
                                    Refunded{order.refundedAt ? ` on ${formatDate(order.refundedAt)}` : ''}
                                    {order.refundId ? ` (${order.refundId})` : ''}. Refunds are processed in the Stripe dashboard; status syncs via
                                    webhook.
                                </p>
                            )}
                        </>
                    )}
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isUpdating || isDeleting}
        />
    );
};

export default OrderModal;
